import { useToast } from '@chakra-ui/react'
import {
  faClock,
  faShieldHalved,
  faWarning,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { erc20Abi, parseUnits } from 'viem'
import {
  useChainId,
  useReadContract,
  useSendTransaction,
  useWriteContract,
} from 'wagmi'
import { networkIsoCodeToWagmiChainMapping } from '../../../../appkit'
import AnimateHeight from '../../../../components/AnimateHeight'
import DefaultModal from '../../../../components/DefaultModal'
import FormCheckbox from '../../../../components/FormCheckbox'
import InfoBox from '../../../../components/InfoBox'
import NetworkStatusIndicator from '../../../../components/NetworkStatusIndicator'
import { useCreateThorchainSwapMutation } from '../../../../redux/services/coreApi'
import { RootState } from '../../../../redux/store'
import { NetworkDto, WalletDto } from '../../../../types/coreApi-types'
import { getThorChainAssetInfo } from './thorChainAssetUtils'
import { ThorChainCurrency } from './thorChainUtils'

interface ThorChainSwapConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  payAmount: number
  payCurrency: ThorChainCurrency
  payAddress: string
  payNetwork: NetworkDto
  receiveAmount: number
  receiveCurrency: ThorChainCurrency
  receiveAddress: string
  receiveNetwork: NetworkDto
  quoteMemo: string
  quoteExpiry: number
  quoteInboundAddress: string
  quoteRouter?: string
  destinationWallet: WalletDto
  quoteFees: number
  rawQuoteData: string
}

const ThorChainSwapConfirmModal: React.FC<ThorChainSwapConfirmModalProps> = ({
  isOpen,
  onClose,
  payAmount,
  payCurrency,
  payAddress,
  payNetwork,
  receiveAmount,
  receiveCurrency,
  receiveAddress,
  receiveNetwork,
  quoteMemo,
  quoteExpiry,
  quoteInboundAddress,
  quoteRouter,
  destinationWallet,
  quoteFees,
  rawQuoteData,
}) => {
  const toast = useToast()
  const [confirmChecked, setConfirmChecked] = useState<boolean>(false)
  const [confirmError, setConfirmError] = useState<string>('')
  const [swapStep, setSwapStep] = useState<'idle' | 'approving' | 'swapping'>(
    'idle'
  )

  // Track transaction hashes that have already been toasted to prevent duplicates
  const toastedTransactionHashes = useRef<Set<string>>(new Set())

  // Get user info from Redux state
  const userInfo = useSelector((state: RootState) => state.session.user)

  // Mutation hook for creating thorchain swap
  const [createThorchainSwap] = useCreateThorchainSwapMutation()

  // Get current chain ID from wallet
  const currentChainId = useChainId()
  const expectedChainId =
    networkIsoCodeToWagmiChainMapping[payNetwork.isoCode ?? '']
  const isCorrectNetwork = currentChainId === expectedChainId

  // THORChain Router ABI (partial - only the functions we need)
  const ROUTER_ABI = [
    {
      inputs: [
        { internalType: 'address payable', name: 'vault', type: 'address' },
        { internalType: 'address', name: 'asset', type: 'address' },
        { internalType: 'uint256', name: 'amount', type: 'uint256' },
        { internalType: 'string', name: 'memo', type: 'string' },
        { internalType: 'uint256', name: 'expiration', type: 'uint256' },
      ],
      name: 'depositWithExpiry',
      outputs: [],
      stateMutability: 'payable',
      type: 'function',
    },
  ] as const

  // Reset checkbox state when modal closes.
  useEffect(() => {
    if (!isOpen) {
      setConfirmChecked(false)
      setConfirmError('')
      setSwapStep('idle')
      // Clear toasted transaction hashes when modal closes
      toastedTransactionHashes.current.clear()
    }
  }, [isOpen])

  // Use wagmi hooks for different transaction types
  const {
    sendTransaction,
    data: sendTxData,
    status: sendTxStatus,
    error: sendTxError,
  } = useSendTransaction()
  const {
    writeContract,
    data: writeTxData,
    status: writeTxStatus,
    error: writeTxError,
  } = useWriteContract()

  // Get asset info using shared utility
  const assetInfo = getThorChainAssetInfo(payCurrency.thorChainAsset)
  const { isBaseAsset: isPayingBaseAsset, tokenContractAddress } = assetInfo

  const tokenDecimals = 18

  // Check current allowance for token (only needed for tokens)
  const { data: currentAllowance } = useReadContract({
    address: tokenContractAddress as `0x${string}`,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [payAddress as `0x${string}`, quoteRouter as `0x${string}`],
    query: {
      enabled: !isPayingBaseAsset && !!tokenContractAddress && !!quoteRouter,
    },
  })

  // Determine the current transaction status and data
  const currentStatus = writeTxStatus !== 'idle' ? writeTxStatus : sendTxStatus
  const currentData = writeTxData || sendTxData
  const currentError = writeTxError || sendTxError

  // Handle transaction status changes with duplicate prevention
  useEffect(() => {
    if (currentStatus === 'success' && currentData) {
      // Prevent duplicate toasts for the same transaction hash
      if (toastedTransactionHashes.current.has(currentData)) {
        return
      }

      // Mark this transaction hash as toasted
      toastedTransactionHashes.current.add(currentData)

      if (swapStep === 'approving') {
        toast({
          title: 'Approval Transaction Sent!',
          description: 'Token approval completed. Now executing swap...',
          status: 'success',
          duration: 3000,
        })
        setSwapStep('swapping')
        // After approval, execute the actual swap
        executeTokenSwap()
      } else {
        toast({
          title: 'Transaction Sent!',
          description: currentData,
          status: 'success',
          duration: 5000,
        })
        setSwapStep('idle')

        // Save the thorchain swap data to the backend
        if (userInfo?.id) {
          const thorchainSwapData = {
            walletIn: payAddress,
            walletOutId: destinationWallet.id,
            amountIn: payAmount,
            amountOut: receiveAmount,
            fees: quoteFees,
            currencyIn: payCurrency.thorChainAsset,
            currencyOut: receiveCurrency.thorChainAsset,
            transactionHash: currentData,
            rawQuoteData: rawQuoteData,
          }

          createThorchainSwap({
            userId: userInfo.id,
            body: thorchainSwapData,
          }).catch(() => {
            // Don't show error to user as the transaction was successful
            // The swap data will be saved on a best-effort basis
          })
        }
      }
    } else if (currentStatus === 'error') {
      toast({
        title:
          swapStep === 'approving'
            ? 'Approval Failed'
            : 'Swap Execution Failed',
        description: currentError?.message,
        status: 'error',
        duration: 5000,
      })
      setSwapStep('idle')
    }
  }, [
    currentData,
    currentStatus,
    currentError,
    swapStep,
    userInfo?.id,
    payAddress,
    destinationWallet.id,
    payAmount,
    receiveAmount,
    quoteFees,
    payCurrency.thorChainAsset,
    receiveCurrency.thorChainAsset,
    rawQuoteData,
    createThorchainSwap,
    toast,
  ])

  const executeSwap = async () => {
    if (!quoteMemo) {
      toast({
        title: 'No quote available',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    // Validate network before executing
    if (!isCorrectNetwork) {
      toast({
        title: 'Wrong network',
        description: `Please switch to ${payNetwork.name} to continue`,
        status: 'error',
        duration: 5000,
      })
      return
    }

    try {
      if (isPayingBaseAsset) {
        // Base asset swap (ETH, BNB, etc.) - send directly to inbound address
        const valueInWei = BigInt(Math.floor(payAmount * 1e18))
        const encoder = new TextEncoder()
        const memoBytes = encoder.encode(quoteMemo)
        const memoHex =
          '0x' +
          Array.from(memoBytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')

        setSwapStep('swapping')
        sendTransaction({
          to: quoteInboundAddress as `0x${string}`,
          value: valueInWei,
          data: memoHex as `0x${string}`,
        })
      } else {
        // Token swap - needs approval first, then router call
        if (!quoteRouter || !tokenContractAddress) {
          throw new Error('Router or token contract address not found')
        }

        const tokenAmount = parseUnits(payAmount.toString(), tokenDecimals)

        // Check if we need approval
        const needsApproval =
          !currentAllowance || currentAllowance < tokenAmount

        if (needsApproval) {
          // First approve the router to spend tokens
          setSwapStep('approving')
          writeContract({
            address: tokenContractAddress as `0x${string}`,
            abi: erc20Abi,
            functionName: 'approve',
            args: [quoteRouter as `0x${string}`, tokenAmount],
          })
        } else {
          // Already approved, execute swap directly
          setSwapStep('swapping')
          executeTokenSwap()
        }
      }
    } catch (err: any) {
      toast({
        title: 'Swap Execution Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
      })
      setSwapStep('idle')
    }
  }

  const executeTokenSwap = async () => {
    if (!quoteRouter || !tokenContractAddress) {
      throw new Error('Router or token contract address not found')
    }

    try {
      const tokenAmount = parseUnits(payAmount.toString(), tokenDecimals)

      writeContract({
        address: quoteRouter as `0x${string}`,
        abi: ROUTER_ABI,
        functionName: 'depositWithExpiry',
        args: [
          quoteInboundAddress as `0x${string}`,
          tokenContractAddress as `0x${string}`,
          tokenAmount,
          quoteMemo,
          BigInt(quoteExpiry),
        ],
      })
    } catch (err: any) {
      toast({
        title: 'Token Swap Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
      })
      setSwapStep('idle')
    }
  }

  const quoteExpiryMoment = moment(quoteExpiry * 1000)

  return (
    <DefaultModal
      isLoading={false}
      isLoadingText="Generating swap..."
      isOpen={isOpen}
      onClose={onClose}
      title="THORChain Swap Confirmation"
      maxW="700px"
    >
      <div className="p-5">
        {/* Network Status Indicator */}
        <NetworkStatusIndicator
          currentChainId={currentChainId}
          expectedNetwork={payNetwork}
          expectedChainId={expectedChainId}
          className="mb-4"
        />

        <h2 className="text-default-small mb-2">Payment method</h2>
        <div className="bg-brand-light-card dark:bg-brand-dark-card grid grid-cols-10 justify-between p-5 rounded-md">
          <div className="col-span-6">
            <p className="text-default flex items-center gap-2">
              <img src={payNetwork?.iconUrl} className="inline-icon" />
              {payAddress}
            </p>
            <p className="text-description mt-1">Address</p>
          </div>
          <div className="col-span-4">
            <div className="flex items-center gap-2">
              <img src={payNetwork?.iconUrl} className="inline-icon" />
              <p className="text-default">{payNetwork.name}</p>
            </div>
            <p className="text-description mt-1">Network</p>
          </div>
        </div>

        <h2 className="mt-4 text-default-small mb-2">Receive method</h2>
        <div className="bg-brand-light-card dark:bg-brand-dark-card grid grid-cols-10 justify-between p-5 rounded-md">
          <div className="col-span-6">
            <p className="text-default flex items-center gap-2">
              <img src={receiveNetwork.iconUrl} className="inline-icon" />
              {receiveAddress}
            </p>
            <p className="text-description mt-1">Address</p>
          </div>
          <div className="col-span-4">
            <div className="flex items-center gap-2">
              <img src={receiveNetwork.iconUrl} className="inline-icon" />
              <p className="text-default">{receiveNetwork.name}</p>
            </div>
            <p className="text-description mt-1">Network</p>
          </div>
        </div>

        <h2 className="mt-4 text-default-small mb-2">Expected swap result</h2>
        <div className="bg-brand-light-card dark:bg-brand-dark-card grid grid-cols-10 justify-between p-5 rounded-md">
          <div className="col-span-6">
            <p className="text-default flex items-center gap-2">
              <img src={payCurrency.iconUrl} className="inline-icon" />
              {payAmount}
            </p>
            <p className="text-description mt-1">Pay amount</p>
          </div>
          <div className="col-span-4">
            <div className="flex items-center gap-2">
              <img src={receiveCurrency.iconUrl} className="inline-icon" />
              <p className="text-default">{receiveAmount}</p>
            </div>
            <p className="text-description mt-1">Receive amount</p>
          </div>
        </div>

        <div className="my-10">
          <div className="flex">
            <div className="w-[36px] text-brand-accent flex-shrink-0">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div>
              <p className="text-default mb-1">
                This pricing quote is only valid untill{' '}
                {quoteExpiryMoment.format('HH:mm')} today.
              </p>
              <p className="text-description">
                If you continue with the swap after this time, the quote is
                invalid and you will not be guaranteed to receive anything.
              </p>
            </div>
          </div>
          <div className="flex mt-4">
            <div className="w-[36px] text-brand-accent flex-shrink-0">
              <FontAwesomeIcon icon={faShieldHalved} />
            </div>
            <div>
              <p className="text-default mb-1">
                THORChain is a third-party provider
              </p>
              <p className="text-description">
                Ensure that the above information is correct and that you are
                able to receive the currency with the selected Receive method.
                The transaction will be executed using THORChain Swap and thus
                if funds are sent incorrectly they will be permanently lost. We
                are not responsible for any lost funds using this method.
              </p>
            </div>
          </div>
          <div className="flex mt-4">
            <div className="w-[36px] text-brand-accent flex-shrink-0">
              <FontAwesomeIcon icon={faWarning} />
            </div>
            <div>
              <p className="text-default mb-1">This is a beta feature</p>
              <p className="text-description">
                Please double-check all details before proceeding, and check
                everything in your wallet connector with regards to what network
                you are connected to and are signing the transaction for. This
                feature should not be trusted with large amounts of funds until
                it has been thoroughly tested.
              </p>
            </div>
          </div>
        </div>

        <FormCheckbox
          renderLabel="I confirm that the details above are correct, and that I am the sole responsible party for the transaction, and I understand that the transaction cannot be reverted."
          onChange={() => {
            if (confirmError && !confirmChecked) {
              setConfirmError('')
            }
            setConfirmChecked(!confirmChecked)
          }}
          checked={confirmChecked}
          error={{ message: confirmError }}
        />

        <button
          className="button mt-6"
          disabled={!confirmChecked || swapStep !== 'idle' || !isCorrectNetwork}
          onClick={async () => {
            if (!confirmChecked) {
              setConfirmError('Required')
              return
            }
            if (!isCorrectNetwork) {
              toast({
                title: 'Wrong network',
                description: `Please switch to ${payNetwork.name} to continue`,
                status: 'warning',
                duration: 5000,
              })
              return
            }
            await executeSwap()
          }}
        >
          {swapStep === 'approving'
            ? 'Approving Token...'
            : swapStep === 'swapping'
              ? 'Executing Swap...'
              : 'Execute swap'}
        </button>

        {currentStatus === 'success' && (
          <AnimateHeight isVisible={currentStatus === 'success'}>
            <InfoBox className="w-min mt-4" colorScheme="green">
              <div className="flex flex-col gap-2">
                <p>Transaction successfully submitted!</p>
                <p className="font-semibold">
                  Transaction hash:{' '}
                  <span className="font-regular">{currentData}</span>
                </p>
              </div>
            </InfoBox>
          </AnimateHeight>
        )}
      </div>
    </DefaultModal>
  )
}

export default ThorChainSwapConfirmModal
